import { CallEventBus } from './events';
import { CallState } from './types';

export type CallMachine = {
	getState: () => CallState;
	subscribe: (listener: (state: CallState) => void) => () => void;
	dispose: () => void;
};

/**
 * Derives CallState from bus events. Subscribers receive the new state synchronously
 * after each emit. The machine itself does not emit — it only listens.
 *
 * After 'call:ended', the machine schedules a microtask to drop back to 'idle' so
 * the UI settles without consumers having to clean up a transient 'ended' state.
 */
export function createCallMachine(bus: CallEventBus): CallMachine {
	let state: CallState = { status: 'uninit' };
	const listeners = new Set<(s: CallState) => void>();

	const setState = (next: CallState) => {
		state = next;
		listeners.forEach(l => l(state));
	};

	const unsubs: Array<() => void> = [
		bus.on('device:registered', () => {
			if (state.status === 'uninit') setState({ status: 'idle' });
		}),
		bus.on('device:unregistered', () => {
			setState({ status: 'uninit' });
		}),
		bus.on('call:incoming', incoming => {
			// Ignore inbound real calls while a Spar practice session is active —
			// the practice session owns the pipeline and shouldn't be clobbered.
			if (state.status === 'practice') return;
			setState({ status: 'ringing', incoming });
		}),
		bus.on('call:dialing', target => {
			setState({ status: 'dialing', target });
		}),
		bus.on('call:connected', call => {
			setState({ status: 'connected', call });
		}),
		bus.on('call:rejected', () => {
			setState({ status: 'idle' });
		}),
		bus.on('call:ended', ({ reason }) => {
			setState({ status: 'ended', reason });
			queueMicrotask(() => {
				if (state.status === 'ended') setState({ status: 'idle' });
			});
		}),
		// A Spar practice session is enterable only from a settled state (idle or
		// the transient ended) so it never interrupts a real in-progress call.
		bus.on('call:practice-started', session => {
			if (state.status === 'idle' || state.status === 'ended') setState({ status: 'practice', session });
		}),
		// Only end practice from the practice state. A late practice-ended must not
		// clobber an intervening transition — e.g. if `device:unregistered` fired
		// mid-practice (→ uninit), this would otherwise wrongly move uninit → idle.
		bus.on('call:practice-ended', () => {
			if (state.status === 'practice') setState({ status: 'idle' });
		}),
	];

	return {
		getState: () => state,
		subscribe: listener => {
			listeners.add(listener);
			return () => listeners.delete(listener);
		},
		dispose: () => {
			unsubs.forEach(u => u());
			listeners.clear();
		},
	};
}
